import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {SupportedValueTypes} from 'benchling-apps/BenchlingAppEditor';
import {
  BenchlingAppType,
  isParentDependencyType,
  SUBDEPENDENCY_KEY_BY_TYPE,
} from 'benchling-apps/BenchlingAppTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import deepCopy from 'lib/deepCopy';
import {GRAY} from 'ui-library/Colors';

import {getBenchlingAppConfigurationQuery} from './__generated/getBenchlingAppConfigurationQuery';
import BenchlingAppConfigurationRow from './BenchlingAppConfigurationRow';
import {SubdependencyArray} from './parent-dependency/BaseSubdependencyList';
import {
  UpdateArrayElementValidationCallback,
  ConfigurationsValidation,
  DependencyConfigValidation,
} from './validation/ValidationTypes';

type Configuration = ExtractByTypename<
  getBenchlingAppConfigurationQuery['benchlingApp'],
  'BenchlingAppNode'
>['configuration'][number];

type ArrayElement = NonNullable<Configuration['elements']>[number];

type ArrayElementConfig = ArrayElement['elementConfigs'][number];

export interface Props {
  configuration: Array<Configuration | ArrayElementConfig>;
  updateConfiguration: React.Dispatch<React.SetStateAction<Array<Configuration | ArrayElementConfig>>>;
  configsValidation: ConfigurationsValidation;
  updateConfigsValidation: React.Dispatch<React.SetStateAction<ConfigurationsValidation>>;
  updateArrayElementValidation: UpdateArrayElementValidationCallback;
}

export default function BenchlingAppConfigurationRowSet({
  configuration,
  updateConfiguration,
  configsValidation,
  updateConfigsValidation,
  updateArrayElementValidation,
}: Props): JSX.Element {
  return (
    <div className={classNames(css(styles.configCard))}>
      {configuration.map((configItem, i) => {
        const subdependencies = getMaybeSubdependencies(configItem);
        return (
          <div key={i} className="u-spacing-top">
            <BenchlingAppConfigurationRow
              {...configItem}
              value={configItem.value}
              elementDefinition={(configItem as Configuration).elementDefinition}
              minElements={(configItem as Configuration).minElements}
              maxElements={(configItem as Configuration).maxElements}
              scalarEnum={configItem.enum}
              onChangeValue={(val) => {
                updateConfiguration(handleSetParentConfigurationState(configuration, i, val));
              }}
              onChangeElements={(newElements) => {
                const newElementState = handleSetElements(configuration, i, newElements);
                updateConfiguration(newElementState);
              }}
              dependencyValidation={configsValidation[configItem.name]}
              updateDependencyConfigValidation={(dependencyValidation: DependencyConfigValidation) => {
                updateDependencyConfigValidation(
                  dependencyValidation,
                  configItem.name,
                  configsValidation,
                  updateConfigsValidation
                );
              }}
              updateArrayElementValidation={updateArrayElementValidation}
              isEditable={true}
              subdependencies={subdependencies}
              onChangeSubdependency={(newSubdependencies) => {
                updateConfiguration(
                  handleSetSubdependencyConfigurationState(configuration, i, newSubdependencies)
                );
              }}
              outputFields={configItem.output?.fieldDefinitions ?? null}
              onChangeOutputFields={(newOutputFields) => {
                updateConfiguration(
                  handleSetOutputFieldsConfigurationState(configuration, i, newOutputFields)
                );
              }}
              elements={(configItem as Configuration).elements}
            />
          </div>
        );
      })}
    </div>
  );
}

function handleSetSubdependencyConfigurationState(
  configuration: Array<Configuration | ArrayElementConfig>,
  i: number,
  newSubdependencies: SubdependencyArray
): Array<Configuration | ArrayElementConfig> {
  const config = configuration[i];
  const newValue = {
    ...config,
    [getSubdependencyKeyForType(config.type)]: newSubdependencies,
  } as Configuration | ArrayElementConfig;
  return writeConfigValueAtIndex(configuration, i, newValue);
}

function handleSetOutputFieldsConfigurationState(
  configuration: Array<Configuration | ArrayElementConfig>,
  i: number,
  newOutputFields: SubdependencyArray
): Array<Configuration | ArrayElementConfig> {
  const newValue = {
    ...configuration[i],
    output: {
      fieldDefinitions: newOutputFields,
      __typename: 'OutputFieldDefinitionNode',
    },
  } as Configuration | ArrayElementConfig;
  return writeConfigValueAtIndex(configuration, i, newValue);
}

function handleSetElements(
  configuration: Array<Configuration | ArrayElementConfig>,
  i: number,
  newElements: Array<ArrayElement>
): Array<Configuration | ArrayElementConfig> {
  const newValue = {
    ...configuration[i],
    elements: newElements,
  };
  return writeConfigValueAtIndex(configuration, i, newValue);
}

function handleSetParentConfigurationState(
  configuration: Array<Configuration | ArrayElementConfig>,
  i: number,
  val: SupportedValueTypes
): React.SetStateAction<Array<Configuration | ArrayElementConfig>> {
  const config = configuration[i];

  let newSubdependencies = getMaybeSubdependencies(config);
  let outputFields = config.output?.fieldDefinitions ?? null;
  // If you change parent dependency to a new value, reset all subdependencies and output fields to null.
  if (val !== config.value) {
    if (newSubdependencies != null) {
      newSubdependencies = newSubdependencies.map((subdependency) => ({
        ...subdependency,
        value: null,
      }));
    }
    if (outputFields != null) {
      outputFields = outputFields.map((field) => ({
        ...field,
        value: null,
      }));
    }
  }

  const newValue = {
    ...config,
    value: val,
    [getSubdependencyKeyForType(config.type)]: newSubdependencies,
    output: outputFields
      ? {
          fieldDefinitions: outputFields,
          __typename: 'OutputFieldDefinitionNode',
        }
      : null,
  } as Configuration | ArrayElementConfig;
  return writeConfigValueAtIndex(configuration, i, newValue);
}

function writeConfigValueAtIndex(
  configuration: Array<Configuration | ArrayElementConfig>,
  i: number,
  newValue: Configuration | ArrayElementConfig
): Array<Configuration | ArrayElementConfig> {
  return [
    ...configuration.slice(0, i),
    {
      ...newValue,
    },
    ...configuration.slice(i + 1),
  ];
}

const updateDependencyConfigValidation = (
  dependencyValidation: DependencyConfigValidation,
  depName: string,
  configsValidation: ConfigurationsValidation,
  updateConfigsValidation: (newConfigsValidation: ConfigurationsValidation) => void
): void => {
  const configsValidationCopy = deepCopy(configsValidation);
  configsValidationCopy[depName] = dependencyValidation;
  updateConfigsValidation(configsValidationCopy);
};

function getMaybeSubdependencies(
  configuration: Configuration | ArrayElementConfig
): SubdependencyArray | null {
  return configuration.options ?? configuration.fieldDefinitions ?? null;
}

function getSubdependencyKeyForType(type: BenchlingAppType): string {
  if (isParentDependencyType(type)) {
    return SUBDEPENDENCY_KEY_BY_TYPE[type];
  } else {
    // Non parent dependency types also have fieldDefinitions.
    return 'fieldDefinitions';
  }
}

const styles = StyleSheet.create({
  configCard: {
    marginTop: '10px',
    overflow: 'scroll',
    paddingRight: '10px',
    '::-webkit-scrollbar': {
      '-webkit-appearance': 'none',
      width: '7px',
    },
    '::-webkit-scrollbar-thumb': {
      'border-radius': '4px',
      'background-color': GRAY,
    },
  },
});
